import dayjs from "dayjs";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useUserProfile } from "@/user/useUserProfiles";
import type { FormReadCorrection } from "./corrections";
import { useJudgeReading, useReviewerAnswer, useSaveReviewerAnswer } from "./useFormReadReview";

const splitItems = (text: string) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const formatReviewedAt = (reviewedAt: string) => dayjs(reviewedAt).tz().format("DD/MM/YY HH:mm");

function WhoAnswered({ reviewedBy, reviewedAt }: { reviewedBy: string; reviewedAt: string }) {
  const { data: profiles, isLoading } = useUserProfile(reviewedBy);
  if (isLoading) return null;

  const name = profiles?.[reviewedBy]?.display_name ?? "an unknown reviewer";

  return (
    <p className="text-xs text-muted-foreground">
      Answered by {name}, {formatReviewedAt(reviewedAt)}
    </p>
  );
}

interface AnswerFormProps {
  row: FormReadCorrection;
  initialItems: string;
  initialNote: string;
}

function AnswerForm({ row, initialItems, initialNote }: AnswerFormProps) {
  const [items, setItems] = useState(initialItems);
  const [note, setNote] = useState(initialNote);
  const save = useSaveReviewerAnswer(row.encounter_id, row.id);

  const submit = () => {
    const trimmedNote = note.trim();
    save.mutate({ items: splitItems(items), ...(trimmedNote && { note: trimmedNote }) });
  };

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="space-y-1">
        <span className="text-xs font-medium text-muted-foreground">Expected items, one per line</span>
        <Textarea
          aria-label="Expected items"
          className="min-h-[96px]"
          value={items}
          onChange={(event) => setItems(event.target.value)}
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs font-medium text-muted-foreground">Note (optional)</span>
        <Textarea
          aria-label="Note"
          className="min-h-[56px]"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      {save.error && <p className="text-sm text-destructive">{save.error.message}</p>}
      <div className="flex items-center gap-3">
        <Button type="button" size="sm" disabled={save.isPending} onClick={submit}>
          Save
        </Button>
        {row.reviewer_answer_key && row.reviewed_by && row.reviewed_at && (
          <WhoAnswered reviewedBy={row.reviewed_by} reviewedAt={row.reviewed_at} />
        )}
      </div>
    </div>
  );
}

const prefill = (row: FormReadCorrection, handwriting: string | undefined) =>
  [...row.corrected_items, ...(handwriting ? [handwriting] : [])].join("\n");

export function ReviewerAnswer({ row }: { row: FormReadCorrection }) {
  const reading = useJudgeReading(row);
  const saved = useReviewerAnswer(row);

  if (saved.error) return <p className="text-sm text-destructive">{saved.error.message}</p>;
  if (reading.isLoading || saved.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const answer = saved.data;
  return (
    <AnswerForm
      key={`${row.id}:${row.reviewer_answer_key}:${row.reviewed_at}`}
      row={row}
      initialItems={answer ? answer.items.join("\n") : prefill(row, reading.data?.handwriting)}
      initialNote={answer?.note ?? ""}
    />
  );
}
